import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Search, Package, Download, Filter, TrendingUp, AlertTriangle, X, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Inventory = () => {
  const { getAuthHeaders, isAdmin } = useAuth();
  const [inventory, setInventory] = useState([]);
  const [depots, setDepots] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedDepot, setSelectedDepot] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Use different endpoint for depot users vs admin
        const endpoint = isAdmin ? '/inventory' : '/inventory/depot-view';
        const params = isAdmin 
          ? { search: undefined, depot_id: selectedDepot !== 'all' ? selectedDepot : undefined }
          : {};
        
        const [invRes, depotsRes] = await Promise.all([
          axios.get(`${API}${endpoint}`, { 
            headers: getAuthHeaders(),
            params
          }),
          isAdmin ? axios.get(`${API}/depots`, { headers: getAuthHeaders() }) : Promise.resolve({ data: [] })
        ]);
        setInventory(invRes.data);
        setDepots(depotsRes.data);
      } catch (error) {
        console.error('Error fetching inventory:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getAuthHeaders, isAdmin, selectedDepot]);

  // Filtered and sorted data
  const filteredInventory = useMemo(() => {
    let items = [...inventory];
    
    // Apply search filter - search by item_name only (SKU may have duplicates)
    if (search.trim()) {
      const query = search.toLowerCase();
      items = items.filter(item => 
        item.item_name?.toLowerCase().includes(query)
      );
    }
    
    // Apply status filter
    if (statusFilter === 'reorder') {
      items = items.filter(item => item.reorder_required);
    } else if (statusFilter === 'ok') {
      items = items.filter(item => !item.reorder_required);
    } else if (statusFilter === 'low_coverage') {
      // Items with less than 1 month coverage but not yet reorder required
      items = items.filter(item => item.months_coverage < 2 && item.months_coverage !== 99);
    }
    
    // Apply sorting
    if (sortConfig.key) {
      items.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal?.toLowerCase() || '';
        }
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return items;
  }, [inventory, search, statusFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1" />
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setSelectedDepot('all');
    setSortConfig({ key: null, direction: 'asc' });
  };

  const hasActiveFilters = search || statusFilter !== 'all' || selectedDepot !== 'all';

  // Summary stats
  const totalItems = filteredInventory.length;
  const reorderItems = filteredInventory.filter(i => i.reorder_required).length;
  const totalStock = filteredInventory.reduce((sum, i) => sum + (i.quantity || 0), 0);

  const exportToExcel = () => {
    const data = isAdmin 
      ? [
          ['SKU', 'Item Name', 'Depot', 'Quantity', 'Buffer Level', 'Status'],
          ...filteredInventory.map(item => [
            item.sku,
            item.item_name,
            item.depot_name,
            item.quantity,
            item.buffer_level,
            item.reorder_required ? 'Reorder Required' : 'OK'
          ])
        ]
      : [
          ['SKU', 'Item Name', 'Current Stock', 'Avg Monthly Sales', 'Months Coverage', '2 Month Requirement', 'Status'],
          ...filteredInventory.map(item => [
            item.sku,
            item.item_name,
            item.quantity,
            item.avg_sales,
            item.months_coverage === 99 ? 'N/A' : item.months_coverage,
            item.two_months_requirement,
            item.reorder_required ? 'Reorder Required' : 'OK'
          ])
        ];
    
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory');
    
    let filename = `inventory_${new Date().toISOString().split('T')[0]}`;
    if (statusFilter !== 'all') filename += `_${statusFilter}`;
    filename += '.xlsx';
    
    XLSX.writeFile(wb, filename);
    toast.success(`Exported ${filteredInventory.length} items to Excel`);
  };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Inventory
          </h1>
          <p className="text-slate-500 mt-1">
            {isAdmin ? 'View stock levels across all depots' : 'View your depot stock levels'}
          </p>
        </div>
        <Button onClick={exportToExcel} variant="outline" data-testid="export-btn">
          <Download className="w-4 h-4 mr-2" />
          Export Excel
        </Button>
      </div>

      {/* Summary Cards (for depot users) */}
      {!isAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="card-hover">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Items</p>
                  <p className="text-2xl font-bold text-slate-900 tabular-nums">
                    {totalItems}
                    {hasActiveFilters && <span className="text-sm font-normal text-slate-400"> of {inventory.length}</span>}
                  </p>
                </div>
                <div className="bg-blue-100 p-2 rounded-lg">
                  <Package className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Stock</p>
                  <p className="text-2xl font-bold text-slate-900 tabular-nums">{totalStock.toLocaleString()}</p>
                </div>
                <div className="bg-green-100 p-2 rounded-lg">
                  <Package className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Need Reorder</p>
                  <p className="text-2xl font-bold text-red-600 tabular-nums">{reorderItems}</p>
                </div>
                <div className="bg-red-100 p-2 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Inventory Table */}
      <Card data-testid="inventory-table">
        <CardHeader className="border-b">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Package className="w-5 h-5" />
                Stock Inventory
                <Badge variant="secondary" className="ml-2">{filteredInventory.length} of {inventory.length} items</Badge>
              </CardTitle>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 hover:text-slate-700">
                  <X className="w-4 h-4 mr-1" />
                  Clear Filters
                </Button>
              )}
            </div>
            
            {/* Search and Filters Row */}
            <div className="flex flex-col md:flex-row gap-3">
              {/* Search Input */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by SKU or Item Name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="inventory-search"
                />
              </div>
              
              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-[180px]" data-testid="status-filter">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Stock Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="reorder">
                    <span className="flex items-center gap-2 text-red-600">Reorder Required</span>
                  </SelectItem>
                  <SelectItem value="ok">
                    <span className="flex items-center gap-2 text-green-600">Stock OK</span>
                  </SelectItem>
                  {!isAdmin && (
                    <SelectItem value="low_coverage">
                      <span className="flex items-center gap-2 text-amber-600">Low Coverage (&lt;2 mo)</span>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              
              {/* Depot Filter (Admin only) */}
              {isAdmin && (
                <Select value={selectedDepot} onValueChange={setSelectedDepot}>
                  <SelectTrigger className="w-full md:w-[200px]" data-testid="depot-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by Depot" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Depots</SelectItem>
                    {depots.map(depot => (
                      <SelectItem key={depot.id} value={depot.id}>{depot.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filteredInventory.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              {hasActiveFilters ? (
                <>
                  <p className="text-slate-500 text-lg">No items match your filters</p>
                  <Button variant="link" onClick={clearFilters} className="mt-2">
                    Clear all filters
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-slate-500 text-lg">No inventory items found</p>
                  <p className="text-slate-400 text-sm">Upload stock data to see inventory</p>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="cursor-pointer hover:bg-slate-100" onClick={() => handleSort('sku')}>
                      <div className="flex items-center">
                        SKU
                        {getSortIcon('sku')}
                      </div>
                    </th>
                    <th className="cursor-pointer hover:bg-slate-100" onClick={() => handleSort('item_name')}>
                      <div className="flex items-center">
                        Item Name
                        {getSortIcon('item_name')}
                      </div>
                    </th>
                    {isAdmin && <th>Depot</th>}
                    <th className="text-right cursor-pointer hover:bg-slate-100" onClick={() => handleSort('quantity')}>
                      <div className="flex items-center justify-end">
                        Current Stock
                        {getSortIcon('quantity')}
                      </div>
                    </th>
                    {isAdmin ? (
                      <th className="text-right cursor-pointer hover:bg-slate-100" onClick={() => handleSort('buffer_level')}>
                        <div className="flex items-center justify-end">
                          Buffer Level
                          {getSortIcon('buffer_level')}
                        </div>
                      </th>
                    ) : (
                      <>
                        <th className="text-right cursor-pointer hover:bg-slate-100" onClick={() => handleSort('avg_sales')}>
                          <div className="flex items-center justify-end">
                            Avg Sales/Mo
                            {getSortIcon('avg_sales')}
                          </div>
                        </th>
                        <th className="text-right cursor-pointer hover:bg-slate-100" onClick={() => handleSort('months_coverage')}>
                          <div className="flex items-center justify-end">
                            Coverage
                            {getSortIcon('months_coverage')}
                          </div>
                        </th>
                      </>
                    )}
                    <th>Status</th>
                    <th>Last Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInventory.map((item) => (
                    <tr 
                      key={item.id} 
                      className={item.reorder_required ? 'reorder-alert' : ''}
                      data-testid={`inventory-row-${item.sku}`}
                    >
                      <td className="font-mono text-sm font-medium">{item.sku}</td>
                      <td className="font-medium">{item.item_name}</td>
                      {isAdmin && <td>{item.depot_name}</td>}
                      <td className="numeric font-bold">{item.quantity?.toLocaleString()}</td>
                      {isAdmin ? (
                        <td className="numeric">{item.buffer_level?.toLocaleString()}</td>
                      ) : (
                        <>
                          <td className="numeric">
                            <div className="flex items-center justify-end gap-1">
                              <TrendingUp className="w-3 h-3 text-slate-400" />
                              {item.avg_sales?.toLocaleString() || 0}
                            </div>
                          </td>
                          <td className="numeric">
                            <span className={`font-medium ${item.months_coverage < 2 ? 'text-red-600' : 'text-green-600'}`}>
                              {item.months_coverage === 99 ? 'N/A' : `${item.months_coverage} mo`}
                            </span>
                          </td>
                        </>
                      )}
                      <td>
                        {item.reorder_required ? (
                          <Badge variant="destructive" className="bg-red-100 text-red-700 border-0">
                            Reorder Required
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-green-100 text-green-700 border-0">
                            OK
                          </Badge>
                        )}
                      </td>
                      <td className="text-slate-500 text-sm">
                        {item.upload_date ? new Date(item.upload_date).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Inventory;
